import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { OnboardingStep, useOnboardingTour } from "@/contexts/OnboardingTourContext";
import { cn } from "@/lib/utils";

type PopoverPosition = {
  top: number;
  left: number;
  placement: "top" | "bottom";
};

const MAX_SELECTOR_ATTEMPTS = 20;
const SELECTOR_RETRY_DELAY = 350;

const overlayRoot = () => (typeof document !== "undefined" ? document.body : null);

const SAFE_INLINE_MARGIN = 16;
const SAFE_BLOCK_MARGIN = 16;

const getViewportMetrics = () => {
  if (typeof window === "undefined") {
    return { width: 0, height: 0, offsetTop: 0, offsetLeft: 0 };
  }
  const viewport = window.visualViewport;
  if (viewport) {
    return {
      width: viewport.width,
      height: viewport.height,
      offsetTop: viewport.offsetTop,
      offsetLeft: viewport.offsetLeft,
    };
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    offsetTop: 0,
    offsetLeft: 0,
  };
};

export const OnboardingTour: React.FC = () => {
  const {
    isOpen,
    currentStep,
    currentStepIndex,
    steps,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
  } = useOnboardingTour();

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const focusableElementsRef = useRef<HTMLElement[]>([]);
  const overlayContainer = overlayRoot();

  const isLastStep = useMemo(() => currentStepIndex === steps.length - 1, [currentStepIndex, steps.length]);

  const computePosition = useCallback(
    (rect: DOMRect | null) => {
      if (!isOpen) return;
      const { width: viewportWidth, height: viewportHeight, offsetTop, offsetLeft } = getViewportMetrics();
      const dialogRect = dialogRef.current?.getBoundingClientRect();
      const dialogHeight = dialogRect?.height ?? 260;
      const dialogWidth = dialogRect?.width ?? 320;

      let placement: "top" | "bottom" = "bottom";

      if (rect) {
        const availableBottom = offsetTop + viewportHeight - rect.bottom;
        const availableTop = rect.top - offsetTop;
        if (availableBottom < dialogHeight + SAFE_BLOCK_MARGIN && availableTop > dialogHeight) {
          placement = "top";
        }
      }

      let top: number;
      if (!rect) {
        top = offsetTop + viewportHeight / 2 - dialogHeight / 2;
      } else if (placement === "bottom") {
        top = rect.bottom + SAFE_BLOCK_MARGIN;
      } else {
        top = rect.top - dialogHeight - SAFE_BLOCK_MARGIN;
      }

      let left: number;
      if (!rect) {
        left = offsetLeft + (viewportWidth - dialogWidth) / 2;
      } else {
        left = rect.left + rect.width / 2 - dialogWidth / 2;
      }

      const minTop = offsetTop + SAFE_BLOCK_MARGIN;
      const maxTop = offsetTop + viewportHeight - dialogHeight - SAFE_BLOCK_MARGIN;
      const minLeft = offsetLeft + SAFE_INLINE_MARGIN;
      const maxLeft = offsetLeft + viewportWidth - dialogWidth - SAFE_INLINE_MARGIN;

      top = Math.max(minTop, Math.min(maxTop, top));
      left = Math.max(minLeft, Math.min(maxLeft, left));

      setPosition({
        top,
        left,
        placement,
      });
    },
    [isOpen]
  );

  const updateRect = useCallback(
    (element: HTMLElement | null) => {
      if (!element) {
        setTargetRect(null);
        setPosition(null);
        computePosition(null);
        return;
      }
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      computePosition(rect);
    },
    [computePosition]
  );

  const focusTrap = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        skipTour();
        return;
      }
      if (event.key !== "Tab") return;
      if (focusableElementsRef.current.length === 0) return;
      const { activeElement } = document;
      const elements = focusableElementsRef.current;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey) {
        if (activeElement === first || !elements.includes(activeElement as HTMLElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [isOpen, skipTour]
  );

  const collectFocusableElements = useCallback(() => {
    if (!dialogRef.current) return;
    const elements = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute("disabled"));
    focusableElementsRef.current = elements;
    requestAnimationFrame(() => {
      elements[0]?.focus();
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    collectFocusableElements();
  }, [isOpen, currentStepIndex, collectFocusableElements]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", focusTrap);
    return () => document.removeEventListener("keydown", focusTrap);
  }, [focusTrap, isOpen]);

  const fallbackToCenter = useCallback(() => {
    setTargetElement(null);
    setTargetRect(null);
    computePosition(null);
  }, [computePosition]);

  const ensureTarget = useCallback(
    (step?: OnboardingStep) => {
      if (!isOpen) return;
      const stepSelector = step?.selector;
      if (!stepSelector) {
        fallbackToCenter();
        return;
      }
      let attempts = 0;
      let cancelled = false;
      const search = () => {
        if (cancelled || !isOpen) return;
        const element = document.querySelector(stepSelector) as HTMLElement | null;
        if (element) {
          if (!step?.preventAutoScroll) {
            element.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
          }
          setTargetElement(element);
          updateRect(element);
          return;
        }
        attempts += 1;
        if (attempts >= MAX_SELECTOR_ATTEMPTS) {
          fallbackToCenter();
          return;
        }
        window.setTimeout(search, SELECTOR_RETRY_DELAY);
      };
      search();
      return () => {
        cancelled = true;
      };
    },
    [fallbackToCenter, isOpen, updateRect]
  );

  useEffect(() => {
    if (!isOpen) {
      setTargetElement(null);
      setTargetRect(null);
      setPosition(null);
      return;
    }
    const cleanup = ensureTarget(currentStep);
    return () => {
      if (typeof cleanup === "function") {
        cleanup();
      }
    };
  }, [isOpen, currentStep, ensureTarget]);

  useEffect(() => {
    if (!isOpen || !currentStep) return;
    if (typeof window === "undefined") return;

    const getScrollContainers = () => {
      if (typeof document === "undefined") return [] as HTMLElement[];
      const containers = new Set<HTMLElement>();
      if (document.scrollingElement instanceof HTMLElement) {
        containers.add(document.scrollingElement);
      }
      if (document.documentElement) {
        containers.add(document.documentElement);
      }
      if (document.body) {
        containers.add(document.body);
      }
      const layoutSurface = document.querySelector(
        "[data-onboarding-scroll-lock-root]"
      ) as HTMLElement | null;
      if (layoutSurface) {
        containers.add(layoutSurface);
      }
      return Array.from(containers);
    };

    const scrollContainers = getScrollContainers();

    if (typeof currentStep.resetScrollTop === "number") {
      const top = currentStep.resetScrollTop;
      window.scrollTo({ top, behavior: "auto" });
      scrollContainers.forEach((container) => {
        if (typeof container.scrollTo === "function") {
          container.scrollTo({ top, behavior: "auto" });
        } else {
          container.scrollTop = top;
        }
      });
    }

    if (!currentStep.lockScroll) return;

    const previousOverflow = scrollContainers.map((container) => container.style.overflow);
    scrollContainers.forEach((container) => {
      container.style.overflow = "hidden";
    });

    return () => {
      scrollContainers.forEach((container, index) => {
        container.style.overflow = previousOverflow[index] ?? "";
      });
    };
  }, [currentStep, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const handle = () => {
      if (targetElement) {
        updateRect(targetElement);
      } else {
        computePosition(null);
      }
    };
    handle();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    window.addEventListener("orientationchange", handle);
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", handle);
    viewport?.addEventListener("scroll", handle);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("orientationchange", handle);
      viewport?.removeEventListener("resize", handle);
      viewport?.removeEventListener("scroll", handle);
    };
  }, [computePosition, isOpen, targetElement, updateRect]);

  useLayoutEffect(() => {
    if (!isOpen || !dialogRef.current) return;
    const observer = new ResizeObserver(() => computePosition(targetRect));
    observer.observe(dialogRef.current);
    return () => observer.disconnect();
  }, [computePosition, isOpen, targetRect]);

  const dialogStyle = useMemo<React.CSSProperties>(() => {
    const base: React.CSSProperties = {
      width: "min(22rem, calc(var(--app-dvw) - var(--overlay-safe-inline) * 2))",
      maxWidth: "min(22rem, calc(var(--app-dvw) - var(--overlay-safe-inline) * 2))",
      maxHeight:
        "calc(var(--app-dvh) - var(--app-safe-area-top) - var(--app-safe-area-bottom) - var(--overlay-safe-block) * 2)",
    };

    if (position) {
      return {
        ...base,
        top: position.top,
        left: position.left,
      };
    }

    return {
      ...base,
      top: "calc(var(--app-viewport-offset-top) + var(--app-safe-area-top) + var(--app-dvh) / 2 - 160px)",
      left: "calc(var(--app-viewport-offset-left) + 50% - 160px)",
    };
  }, [position]);

  if (!isOpen || !overlayContainer) {
    return null;
  }

  const ariaLabelledBy = currentStep ? `onboarding-step-${currentStep.id}` : undefined;

  return createPortal(
    <div className="fixed inset-0 z-[9998]">
      <div className="fixed inset-0 bg-slate-950/70 transition-opacity" aria-hidden="true" />
      {targetRect && (
        <div
          className="pointer-events-none fixed z-[9999] rounded-xl shadow-[0_0_0_200vmax_rgba(15,23,42,0.65)] transition-all"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      )}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        className={cn(
          "overlay-surface overlay-elevated overlay-scrollable overlay-dimensions overlay-frame fixed z-[10000] border border-white/10 bg-white text-slate-900 shadow-xl transition-all",
          "focus:outline-none"
        )}
        style={dialogStyle}
      >
        {currentStep && (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-500">
                Paso {currentStepIndex + 1} de {steps.length}
              </p>
              <h2 id={ariaLabelledBy} className="text-xl font-semibold">
                {currentStep.title}
              </h2>
              <div className="text-sm leading-relaxed text-slate-600">{currentStep.body}</div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={skipTour}>
                  Saltar
                </Button>
                <Button variant="ghost" size="sm" onClick={previousStep} disabled={currentStepIndex === 0}>
                  Atrás
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {!isLastStep && (
                  <Button size="sm" onClick={nextStep}>
                    Siguiente
                  </Button>
                )}
                {isLastStep && (
                  <Button size="sm" onClick={completeTour}>
                    Finalizar
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    overlayContainer
  );
};
